'use client';

/**
 * Section — v4 page/section shell.
 * Wraps content in the `.v4-section` surface + `.v4-container`, optionally
 * renders the GlowBackground backdrop and a heading (eyebrow / title / subtitle).
 */

import type { CSSProperties, ReactNode } from 'react';
import { GlowBackground, type GlowBackgroundProps } from '../theme/GlowBackground';

export interface SectionProps {
  children: ReactNode;
  /** Small uppercase kicker above the title. */
  eyebrow?: ReactNode;
  /** Main heading. */
  title?: ReactNode;
  /** Sub-line under the heading. */
  subtitle?: ReactNode;
  /** Render the signature glow backdrop. Default true. */
  glow?: boolean;
  /** Props forwarded to GlowBackground when glow is on. */
  glowProps?: GlowBackgroundProps;
  /** Center the heading block. */
  centered?: boolean;
  /** Vertical padding inside the container. Default "56px 0". */
  padding?: string;
  className?: string;
  style?: CSSProperties;
  containerStyle?: CSSProperties;
}

export function Section({
  children,
  eyebrow,
  title,
  subtitle,
  glow = true,
  glowProps,
  centered = false,
  padding = '56px 0',
  className,
  style,
  containerStyle,
}: SectionProps) {
  const classes = ['v4-section', className ?? ''].filter(Boolean).join(' ');
  const hasHeader = eyebrow || title || subtitle;

  return (
    <section className={classes} style={{ minHeight: '100%', ...style }}>
      {glow && <GlowBackground {...glowProps} />}
      <div
        className="v4-container"
        style={{ padding, textAlign: centered ? 'center' : undefined, ...containerStyle }}
      >
        {hasHeader && (
          <header style={{ marginBottom: 'var(--v4-s8)' }}>
            {eyebrow && <p className="v4-eyebrow">{eyebrow}</p>}
            {title && <h1 className="v4-title">{title}</h1>}
            {subtitle && <p className="v4-subtitle">{subtitle}</p>}
          </header>
        )}
        {children}
      </div>
    </section>
  );
}

export default Section;
